import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "../Loader";
import { fetchDashboard } from "../../features/dashboard/dashboardSlice";
import CountUp from 'react-countup';

const DashboardLayer = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Dashboard data
  const dashboardData = useSelector((state) => state.dashboard?.dashboardData);
  const dashboardStatus = useSelector((state) => state.dashboard?.status);
  const dashboardError = useSelector((state) => state.dashboard?.error);

  useEffect(() => {
    if (dashboardStatus === 'idle') {
      dispatch(fetchDashboard());
    }

  }, [dispatch, dashboardStatus]);

  // Reusable function to create statistics cards
  // value must be numeric; suffix is appended after the value (e.g., currency);
  // useCompact toggles K/M formatting via formattingFn
  const createStatCard = (
    title,
    value,
    icon,
    bgColor,
    textColor,
    subtitle,
    suffix = "",
    useCompact = true,
    tooltip = null,
    onClick = null
  ) => (
    <div className="col-xxl-3 col-lg-4 col-sm-6 mb-32">
      <div 
        className={`card border-0 shadow-sm h-100 ${tooltip ? 'position-relative' : ''} ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
        style={onClick ? { cursor: 'pointer', transition: 'all 0.3s ease' } : {}}
        onMouseEnter={onClick ? (e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.15)'; } : undefined}
        onMouseLeave={onClick ? (e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)'; } : undefined}
      >
        {tooltip && (
          <div className="tooltip-container">
            <div className="tooltip-content">
              {tooltip}
            </div>
          </div>
        )}
        <div className="card-body p-16 position-relative">
          <div className="d-flex align-items-center">
            <div className="flex-shrink-0">
              <div className={`bg-${bgColor} bg-opacity-10 rounded-circle p-3`}>
                <i className={`${icon} text-${textColor} fs-2`}></i>
              </div>
            </div>
            <div className="flex-grow-1 ms-3">
              <div className="d-flex justify-content-between">
                <h6 className="fw-semibold mb-0">{title}</h6>
                {title === "Listed Cars" && (
                  <div className="position-absolute end-0 top-16 d-flex flex-column ms-3 me-1 sm:m-3 gap-1" style={{ zIndex: 10 }}>
                    <div className="d-flex align-items-center text-success">
                      <i className="ri-check-line me-1"></i>
                      <small>
                        <CountUp end={dashboardData?.enlistCar.filter(v => v.approvalStatus === "approved").length || 0} duration={1.5} />
                      </small>
                    </div>
                    <div className="d-flex align-items-center text-warning">
                      <i className="ri-time-line me-1"></i>
                      <small>
                        <CountUp end={dashboardData?.enlistCar.filter(v => v.approvalStatus === "pending").length || 0} duration={1.5} />
                      </small>
                    </div>
                    <div className="d-flex align-items-center text-danger">
                      <i className="ri-close-line me-1"></i>
                      <small>
                        <CountUp end={dashboardData?.enlistCar.filter(v => v.approvalStatus === "rejected").length || 0} duration={1.5} />
                      </small>
                    </div>
                  </div>
                )}
              </div>
              <h3 className={`mb-0 fw-bold text-${textColor}`}>
                <CountUp
                  end={Number(value) || 0}
                  duration={1.5}
                  separator="," 
                  suffix={suffix ? ` ${suffix}` : ""}
                  formattingFn={useCompact ? formatNumber : undefined}
                />
              </h3>
              <small className="text-success d-block mb-2">
                <i className="ri-arrow-up-line"></i> {subtitle}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Function to calculate total booked cars
  const calculateTotalBooked = () => {
    const enlistedBooked = dashboardData?.enlistCar?.filter(v => v.booked === true)?.length || 0;
    // const carsBooked = dashboardData?.cars?.filter(v => v.booked === true)?.length || 0;
    // return enlistedBooked + carsBooked;
    return enlistedBooked;
  };

  // Function to calculate total dealers
  const calculateTotalDealers = () => {
    return dashboardData?.users?.filter(v => v.asDealers === true)?.length || 0;
  };

  // Function to calculate total buyers
  const calculateTotalBuyers = () => {
    return dashboardData?.users?.filter(v => v.asDealers !== true)?.length || 0;
    // return formatNumber(dashboardData?.users?.filter(v => v.asDealers !== true)?.length || 0);
  };

  // Function to format numbers to K and M format
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const calculateTotalRevenue = () => {
    return dashboardData?.enlistCar
      ?.filter((v) => v.booked === true)
      ?.map((v) => +v.price)
      ?.reduce((acc, curr) => acc + curr, 0) || 0;
  };

  if (dashboardStatus === 'loading') return <Loader />;
  if (dashboardStatus === 'failed') return <p>Error: {dashboardError}</p>;

  return (
    <div className="dashboard-container">
      
      {/* Welcome Message */}
      <div className="row mb-32">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4 py-20">
              <div className="text-center">
                <h4 className="text-primary-600 mb-3 ">
                  <i className="ri-dashboard-line me-2"></i>
                  Welcome to Carbeez Dashboard
                </h4>
                <p className="fw-semibold mb-0">
                  Here's an overview of your platform's key metrics and performance indicators.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Statistics Cards */}
      <div className="row mb-4">

        {createStatCard(
          "Listed Cars",
          (dashboardData?.enlistCar?.filter(v => v.booked === false)?.length) || 0,
          "ri-add-circle-line",
          "success",
          "success",
          "New requests"
        )}

        {createStatCard(
          "Total Users",
          dashboardData?.userCount || 0,
          "ri-group-line",
          "warning",
          "warning",
          "Registered users"
        )}

        {createStatCard(
          "Total Dealers",
          calculateTotalDealers(),
          "ri-group-2-line",
          "warning",
          "warning",
          "Registered dealers",
        )}

        {createStatCard(
          "Total Buyers",
          calculateTotalBuyers(),
          "ri-user-star-line",
          "warning",
          "warning",
          "Registered buyers"
        )}

        {createStatCard(
          "Total Booked",
          calculateTotalBooked(),
          "ri-book-line",
          "info",
          "info",
          "This month",
          "",
          true,
          null,
          // () => navigate("/car-listing-booked")
        )}

      {createStatCard(
        "Total Revenue",
        calculateTotalRevenue(),
        "ri-money-dollar-circle-line",
        "info",
        "info",
        "This month",
        "AED",
        true
      )}
      </div>

      {/* Booked Cars with Buyer Emails */}
      {dashboardData?.enlistCar?.filter(v => v.booked === true)?.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div 
              className="card border-0"
              style={{
                background: '#ffffff',
                borderRadius: '20px',
                borderTop: '5px solid transparent',
                borderImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #0dcaf0 50%, #20c997 100%)',
                borderImageSlice: 1,
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                overflow: 'hidden',
                position: 'relative',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 20px 60px rgba(102, 126, 234, 0.15), 0 8px 20px rgba(13, 202, 240, 0.12)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Subtle background pattern */}
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '120px',
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(13, 202, 240, 0.03) 50%, rgba(32, 201, 151, 0.03) 100%)',
                  opacity: 0.6,
                  zIndex: 0
                }}
              ></div>

              <div className="card-body p-5 p-md-4 p-sm-3" style={{ position: 'relative', zIndex: 1 }}>
                {/* Premium Header - Responsive */}
                <div 
                  className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-5 mb-md-4 mb-sm-3 pb-4 pb-md-3 pb-sm-2"
                  style={{ 
                    borderBottom: '3px solid transparent',
                    borderImage: 'linear-gradient(90deg, transparent 0%, rgba(13, 202, 240, 0.2) 50%, transparent 100%)',
                    borderImageSlice: 1,
                  }}
                >
                  <div className="d-flex align-items-center gap-4 gap-md-3 gap-sm-2 mb-3 mb-md-0">
                    <div 
                      className="d-flex align-items-center justify-content-center rounded-circle position-relative booked-cars-icon"
                      style={{
                        width: '64px',
                        height: '64px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #0dcaf0 100%)',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35), 0 0 0 8px rgba(102, 126, 234, 0.1)',
                        animation: 'pulse 2s ease-in-out infinite',
                        flexShrink: 0
                      }}
                    >
                      <i className="ri-book-3-line text-white" style={{ fontSize: '28px' }}></i>
                    </div>
                    <div>
                      <h4 className="mb-1 fw-bold booked-cars-title" style={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #0dcaf0 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontSize: '1.5rem',
                        letterSpacing: '-0.5px'
                      }}>
                        Booked Cars with Buyer Information
                      </h4>
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <div 
                          className="rounded-pill px-3 py-1"
                          style={{
                            background: 'rgba(13, 202, 240, 0.1)',
                            backdropFilter: 'blur(10px)',
                          }}
                        >
                          <small className="text-muted d-flex align-items-center gap-2 fw-medium" style={{ fontSize: '0.875rem' }}>
                            <i className="ri-information-line" style={{ color: '#0dcaf0' }}></i>
                            <span className="d-none d-sm-inline">Showing recent bookings</span>
                            <span className="d-sm-none">Recent bookings</span>
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3 gap-md-2 gap-sm-2 flex-wrap">
                    <span 
                      className="badge rounded-pill px-4 px-md-3 px-sm-2 py-2 booked-count-badge"
                      style={{
                        background: 'linear-gradient(135deg, rgba(13, 202, 240, 0.15) 0%, rgba(32, 201, 151, 0.15) 100%)',
                        backdropFilter: 'blur(10px)',
                        border: '1.5px solid rgba(13, 202, 240, 0.3)',
                        fontSize: '0.9rem',
                        fontWeight: '700',
                        color: '#0dcaf0',
                        boxShadow: '0 4px 12px rgba(13, 202, 240, 0.15)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <i className="ri-checkbox-circle-fill" style={{ fontSize: '18px' }}></i>
                      <span>{dashboardData.enlistCar.filter(v => v.booked === true).length} Booked</span>
                    </span>
                    <button 
                      className="btn booked-view-all-btn"
                      onClick={() => navigate("/car-listing-booked")}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #0dcaf0 100%)',
                        border: 'none',
                        color: 'white',
                        fontWeight: '700',
                        padding: '0.75rem 1.75rem',
                        borderRadius: '12px',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.35)',
                        fontSize: '0.95rem',
                        letterSpacing: '0.3px',
                        position: 'relative',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.5)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.35)';
                      }}
                    >
                      <i className="ri-eye-line me-2"></i>
                      <span className="d-none d-sm-inline">View All</span>
                      <span className="d-sm-none">View</span>
                      <i className="ri-arrow-right-line ms-2"></i>
                    </button>
                  </div>
                </div>

                {/* Premium Table - Responsive */}
                <div 
                  className="table-responsive booked-cars-table-wrapper" 
                  style={{ 
                    borderRadius: '16px', 
                    overflowX: 'auto',
                    overflowY: 'visible',
                    background: '#ffffff',
                    boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.02)',
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  <table 
                    className="table mb-0 booked-cars-table"
                    style={{
                      backgroundColor: 'transparent',
                      borderCollapse: 'separate',
                      borderSpacing: 0,
                      minWidth: '800px'
                    }}
                  >
                    <thead>
                      <tr style={{ 
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(13, 202, 240, 0.08) 50%, rgba(32, 201, 151, 0.08) 100%)',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <th 
                          className="fw-bold text-dark py-4 px-4 booked-table-header booked-col-title"
                          style={{ 
                            borderBottom: '3px solid',
                            borderImage: 'linear-gradient(90deg, transparent 0%, rgba(13, 202, 240, 0.5) 50%, transparent 100%)',
                            borderImageSlice: 1,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontWeight: '800',
                            minWidth: '180px'
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: '32px',
                                height: '32px',
                                background: 'linear-gradient(135deg, #667eea 0%, #0dcaf0 100%)',
                                boxShadow: '0 2px 8px rgba(13, 202, 240, 0.3)',
                                flexShrink: 0
                              }}
                            >
                              <i className="ri-car-line text-white" style={{ fontSize: '14px' }}></i>
                            </div>
                            <span>Car Title</span>
                          </div>
                        </th>
                        <th 
                          className="fw-bold text-dark py-4 px-4 booked-table-header booked-col-make"
                          style={{ 
                            borderBottom: '3px solid',
                            borderImage: 'linear-gradient(90deg, transparent 0%, rgba(13, 202, 240, 0.5) 50%, transparent 100%)',
                            borderImageSlice: 1,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontWeight: '800',
                            minWidth: '120px'
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: '32px',
                                height: '32px',
                                background: 'linear-gradient(135deg, #667eea 0%, #0dcaf0 100%)',
                                boxShadow: '0 2px 8px rgba(13, 202, 240, 0.3)',
                                flexShrink: 0
                              }}
                            >
                              <i className="ri-roadster-line text-white" style={{ fontSize: '14px' }}></i>
                            </div>
                            <span>Make/Model</span>
                          </div>
                        </th>
                        <th 
                          className="fw-bold text-dark py-4 px-4 booked-table-header booked-col-year"
                          style={{ 
                            borderBottom: '3px solid',
                            borderImage: 'linear-gradient(90deg, transparent 0%, rgba(13, 202, 240, 0.5) 50%, transparent 100%)',
                            borderImageSlice: 1,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontWeight: '800',
                            minWidth: '100px'
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: '32px',
                                height: '32px',
                                background: 'linear-gradient(135deg, #667eea 0%, #0dcaf0 100%)',
                                boxShadow: '0 2px 8px rgba(13, 202, 240, 0.3)',
                                flexShrink: 0
                              }}
                            >
                              <i className="ri-calendar-line text-white" style={{ fontSize: '14px' }}></i>
                            </div>
                            <span>Year</span>
                          </div>
                        </th>
                        <th 
                          className="fw-bold text-dark py-4 px-4 booked-table-header booked-col-price"
                          style={{ 
                            borderBottom: '3px solid',
                            borderImage: 'linear-gradient(90deg, transparent 0%, rgba(13, 202, 240, 0.5) 50%, transparent 100%)',
                            borderImageSlice: 1,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontWeight: '800',
                            minWidth: '130px'
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: '32px',
                                height: '32px',
                                background: 'linear-gradient(135deg, #667eea 0%, #0dcaf0 100%)',
                                boxShadow: '0 2px 8px rgba(13, 202, 240, 0.3)',
                                flexShrink: 0
                              }}
                            >
                              <i className="ri-money-dollar-circle-line text-white" style={{ fontSize: '14px' }}></i>
                            </div>
                            <span>Price</span>
                          </div>
                        </th>
                        <th 
                          className="fw-bold text-dark py-4 px-4 booked-table-header booked-col-seller d-none d-md-table-cell"
                          style={{ 
                            borderBottom: '3px solid',
                            borderImage: 'linear-gradient(90deg, transparent 0%, rgba(13, 202, 240, 0.5) 50%, transparent 100%)',
                            borderImageSlice: 1,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontWeight: '800',
                            minWidth: '200px'
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: '32px',
                                height: '32px',
                                background: 'linear-gradient(135deg, #667eea 0%, #0dcaf0 100%)',
                                boxShadow: '0 2px 8px rgba(13, 202, 240, 0.3)',
                                flexShrink: 0
                              }}
                            >
                              <i className="ri-user-line text-white" style={{ fontSize: '14px' }}></i>
                            </div>
                            <span>Seller Email</span>
                          </div>
                        </th>
                        <th 
                          className="fw-bold text-dark py-4 px-4 booked-table-header booked-col-buyer"
                          style={{ 
                            borderBottom: '3px solid',
                            borderImage: 'linear-gradient(90deg, transparent 0%, rgba(13, 202, 240, 0.5) 50%, transparent 100%)',
                            borderImageSlice: 1,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontWeight: '800',
                            minWidth: '200px'
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: '32px',
                                height: '32px',
                                background: 'linear-gradient(135deg, #667eea 0%, #0dcaf0 100%)',
                                boxShadow: '0 2px 8px rgba(13, 202, 240, 0.3)',
                                flexShrink: 0
                              }}
                            >
                              <i className="ri-user-star-line text-white" style={{ fontSize: '14px' }}></i>
                            </div>
                            <span>Buyer Email</span>
                          </div>
                        </th>
                        <th 
                          className="fw-bold text-dark py-4 px-4 booked-table-header booked-col-payment d-none d-lg-table-cell"
                          style={{ 
                            borderBottom: '3px solid',
                            borderImage: 'linear-gradient(90deg, transparent 0%, rgba(13, 202, 240, 0.5) 50%, transparent 100%)',
                            borderImageSlice: 1,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontWeight: '800',
                            minWidth: '150px'
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: '32px',
                                height: '32px',
                                background: 'linear-gradient(135deg, #667eea 0%, #0dcaf0 100%)',
                                boxShadow: '0 2px 8px rgba(13, 202, 240, 0.3)',
                                flexShrink: 0
                              }}
                            >
                              <i className="ri-bank-card-line text-white" style={{ fontSize: '14px' }}></i>
                            </div>
                            <span>Payment Method</span>
                          </div>
                        </th>
                        <th 
                          className="fw-bold text-dark py-4 px-4 booked-table-header booked-col-date d-none d-xl-table-cell"
                          style={{ 
                            borderBottom: '3px solid',
                            borderImage: 'linear-gradient(90deg, transparent 0%, rgba(13, 202, 240, 0.5) 50%, transparent 100%)',
                            borderImageSlice: 1,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontWeight: '800',
                            minWidth: '180px'
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: '32px',
                                height: '32px',
                                background: 'linear-gradient(135deg, #667eea 0%, #0dcaf0 100%)',
                                boxShadow: '0 2px 8px rgba(13, 202, 240, 0.3)',
                                flexShrink: 0
                              }}
                            >
                              <i className="ri-time-line text-white" style={{ fontSize: '14px' }}></i>
                            </div>
                            <span>Booked Date</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.enlistCar
                        .filter(v => v.booked === true)
                        .slice(0, 10) // Show first 10 booked cars
                        .map((car, idx) => {
                          // Get seller email using the same method as buyer email
                          // First, try to get seller userId (could be string or object)
                          const sellerUserId = 
                            (typeof car?.userId === 'object' && car?.userId?._id) || 
                            (typeof car?.userId === 'string' ? car?.userId : null) ||
                            (typeof car?.user === 'object' && car?.user?._id) ||
                            (typeof car?.user === 'string' ? car?.user : null);
                          
                          // Look up seller in dashboardData.users array
                          let sellerEmail = null;
                          if (sellerUserId && dashboardData?.users) {
                            const sellerUser = dashboardData.users.find(
                              user => user._id === sellerUserId || 
                                     user._id?.toString() === sellerUserId?.toString() ||
                                     (typeof sellerUserId === 'string' && user._id?.toString() === sellerUserId)
                            );
                            sellerEmail = sellerUser?.email || null;
                          }
                          
                          // Fallback to direct email fields or object properties
                          if (!sellerEmail) {
                            sellerEmail = 
                              (typeof car?.userId === 'object' && car?.userId?.email) ||
                              (typeof car?.user === 'object' && car?.user?.email) ||
                              car?.sellerEmail ||
                              car?.email ||
                              null;
                          }
                          
                          // Final fallback
                          sellerEmail = sellerEmail || "N/A";
                          
                          // Get buyer email using the same method as seller email
                          // First, try to get buyer userId (could be string or object)
                          const buyerUserId = 
                            (typeof car?.buyerId === 'object' && car?.buyerId?._id) || 
                            (typeof car?.buyerId === 'string' ? car?.buyerId : null) ||
                            car?.BookedBy || 
                            car?.buyer;
                          
                          // Look up buyer in dashboardData.users array (same pattern as seller lookup)
                          let buyerEmail = null;
                          if (buyerUserId && dashboardData?.users) {
                            const buyerUser = dashboardData.users.find(
                              user => user._id === buyerUserId || 
                                     user._id?.toString() === buyerUserId?.toString() ||
                                     (typeof buyerUserId === 'string' && user._id?.toString() === buyerUserId)
                            );
                            buyerEmail = buyerUser?.email || null;
                          }
                          
                          // Fallback to direct email fields or object properties
                          if (!buyerEmail) {
                            buyerEmail = 
                              (typeof car?.buyerId === 'object' && car?.buyerId?.email) ||
                              (typeof car?.buyer === 'object' && car?.buyer?.email) ||
                              car?.buyerEmail || 
                              car?.bookedBy?.email || 
                              car?.submission?.bookedBy?.email ||
                              car?.purchasedBy?.email ||
                              (typeof car?.BookedBy === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(car.BookedBy) ? car.BookedBy : null) ||
                              (typeof car?.buyer === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(car.buyer) ? car.buyer : null) ||
                              "N/A";
                          }
                          
                          // Get payment method
                          const paymentMethod = car?.paymentMethod || car?.submission?.paymentMethod || "N/A";
                          
                          // Get booked date
                          const bookedAtDate = car?.bookedAtDate || car?.submission?.bookedAtDate || null;
                          const formattedBookedDate = bookedAtDate 
                            ? new Date(bookedAtDate).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : "N/A";

                          const isEven = idx % 2 === 0;
                          
                          return (
                            <tr 
                              key={car._id || idx}
                              style={{
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                                backgroundColor: isEven ? '#ffffff' : 'rgba(102, 126, 234, 0.02)',
                                position: 'relative'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(13, 202, 240, 0.08) 100%)';
                                e.currentTarget.style.transform = 'scale(1.005)';
                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.15)';
                                e.currentTarget.style.borderLeft = '4px solid #667eea';
                                e.currentTarget.style.paddingLeft = 'calc(1rem - 4px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = isEven ? '#ffffff' : 'rgba(102, 126, 234, 0.02)';
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.borderLeft = 'none';
                                e.currentTarget.style.paddingLeft = '';
                              }}
                            >
                              <td className="py-4 px-4 booked-col-title">
                                <div className="d-flex align-items-center gap-3 gap-md-2 gap-sm-2">
                                  <div 
                                    className="rounded-circle d-flex align-items-center justify-content-center position-relative"
                                    style={{
                                      width: '44px',
                                      height: '44px',
                                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #0dcaf0 100%)',
                                      flexShrink: 0,
                                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                      transition: 'all 0.3s ease'
                                    }}
                                  >
                                    <i className="ri-car-line text-white" style={{ fontSize: '18px' }}></i>
                                  </div>
                                  <strong className="text-dark" style={{ fontSize: '0.95rem', fontWeight: '600', wordBreak: 'break-word' }}>
                                    {car.listingTitle || car.title || "N/A"}
                                  </strong>
                                </div>
                              </td>
                              <td className="py-4 px-4 booked-col-make">
                                <div className="d-flex flex-column">
                                  <span className="text-dark fw-bold" style={{ fontSize: '0.9rem' }}>
                                    {car.make || "N/A"}
                                  </span>
                                  {car.model && (
                                    <span className="text-muted" style={{ fontSize: '0.8rem', fontWeight: '500' }}>
                                      {car.model}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-4 px-4 booked-col-year">
                                <span 
                                  className="badge rounded-pill px-3 py-2 d-inline-flex align-items-center justify-content-center"
                                  style={{ 
                                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(13, 202, 240, 0.15) 100%)',
                                    color: '#667eea',
                                    fontWeight: '700',
                                    fontSize: '0.85rem',
                                    border: '1.5px solid rgba(102, 126, 234, 0.2)',
                                    minWidth: '60px',
                                    boxShadow: '0 2px 6px rgba(102, 126, 234, 0.1)'
                                  }}
                                >
                                  {car.year || "N/A"}
                                </span>
                              </td>
                              <td className="py-4 px-4 booked-col-price">
                                <span 
                                  className="badge rounded-pill px-4 py-2 d-inline-flex align-items-center gap-2"
                                  style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #0dcaf0 100%)',
                                    color: 'white',
                                    fontWeight: '700',
                                    fontSize: '0.9rem',
                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.35)',
                                    border: 'none',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  <i className="ri-money-dollar-circle-line" style={{ fontSize: '16px' }}></i>
                                  <span>{Number(car.askingPrice || car.price || 0).toLocaleString()} AED</span>
                                </span>
                              </td>
                              <td className="py-4 px-4 booked-col-seller d-none d-md-table-cell">
                                <div 
                                  className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                                  style={{
                                    background: 'rgba(0, 0, 0, 0.03)',
                                    maxWidth: '100%'
                                  }}
                                >
                                  <i className="ri-mail-line" style={{ color: '#6c757d', fontSize: '16px', flexShrink: 0 }}></i>
                                  <small 
                                    className="text-muted fw-medium" 
                                    style={{ 
                                      fontSize: '0.85rem',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '200px'
                                    }}
                                    title={sellerEmail}
                                  >
                                    {sellerEmail}
                                  </small>
                                </div>
                              </td>
                              <td className="py-4 px-4">
                                {buyerEmail !== "N/A" ? (
                                  <span 
                                    className="badge rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2"
                                    style={{
                                      background: 'linear-gradient(135deg, #20c997 0%, #28a745 100%)',
                                      color: 'white',
                                      fontWeight: '700',
                                      fontSize: '0.85rem',
                                      boxShadow: '0 4px 12px rgba(32, 201, 151, 0.35)',
                                      maxWidth: '100%',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      border: 'none'
                                    }}
                                    title={buyerEmail}
                                  >
                                    <i className="ri-mail-check-line" style={{ fontSize: '16px' }}></i>
                                    <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {buyerEmail}
                                    </span>
                                  </span>
                                ) : (
                                  <span 
                                    className="badge rounded-pill px-3 py-2"
                                    style={{
                                      background: 'rgba(108, 117, 125, 0.1)',
                                      color: '#6c757d',
                                      fontWeight: '600'
                                    }}
                                  >
                                    <i className="ri-close-circle-line me-1"></i>N/A
                                  </span>
                                )}
                              </td>
                              <td className="py-4 px-4 booked-col-payment d-none d-lg-table-cell">
                                {paymentMethod !== "N/A" ? (
                                  <span 
                                    className="badge rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2"
                                    style={{
                                      background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                                      color: '#1a1a1a',
                                      fontWeight: '700',
                                      fontSize: '0.85rem',
                                      boxShadow: '0 4px 12px rgba(255, 193, 7, 0.35)',
                                      border: 'none'
                                    }}
                                  >
                                    <i className="ri-bank-card-line" style={{ fontSize: '16px' }}></i>
                                    <span>{paymentMethod.toUpperCase()}</span>
                                  </span>
                                ) : (
                                  <span 
                                    className="badge rounded-pill px-3 py-2"
                                    style={{
                                      background: 'rgba(108, 117, 125, 0.1)',
                                      color: '#6c757d',
                                      fontWeight: '600'
                                    }}
                                  >
                                    <i className="ri-close-circle-line me-1"></i>N/A
                                  </span>
                                )}
                              </td>
                              <td className="py-4 px-4 booked-col-date d-none d-xl-table-cell">
                                {formattedBookedDate !== "N/A" ? (
                                  <div 
                                    className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                                    style={{
                                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(13, 202, 240, 0.08) 100%)',
                                      border: '1px solid rgba(102, 126, 234, 0.2)'
                                    }}
                                  >
                                    <div 
                                      className="rounded-circle d-flex align-items-center justify-content-center"
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #0dcaf0 100%)',
                                        flexShrink: 0,
                                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                                      }}
                                    >
                                      <i className="ri-calendar-check-line text-white" style={{ fontSize: '14px' }}></i>
                                    </div>
                                    <small className="text-dark fw-semibold" style={{ fontSize: '0.85rem' }}>
                                      {formattedBookedDate}
                                    </small>
                                  </div>
                                ) : (
                                  <span 
                                    className="badge rounded-pill px-3 py-2"
                                    style={{
                                      background: 'rgba(108, 117, 125, 0.1)',
                                      color: '#6c757d',
                                      fontWeight: '600'
                                    }}
                                  >
                                    <i className="ri-close-circle-line me-1"></i>N/A
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                {/* Premium Footer Button */}
                {dashboardData.enlistCar.filter(v => v.booked === true).length > 10 && (
                  <div 
                    className="text-center mt-5 pt-4"
                    style={{ 
                      borderTop: '3px solid transparent',
                      borderImage: 'linear-gradient(90deg, transparent 0%, rgba(13, 202, 240, 0.2) 50%, transparent 100%)',
                      borderImageSlice: 1,
                    }}
                  >
                    <button 
                      className="btn px-5 py-3"
                      onClick={() => navigate("/car-listing-booked")}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #0dcaf0 100%)',
                        border: 'none',
                        color: 'white',
                        fontWeight: '700',
                        borderRadius: '14px',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                        fontSize: '1rem',
                        letterSpacing: '0.5px',
                        position: 'relative',
                        overflow: 'hidden',
                        padding: '0.875rem 2rem'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.5)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.4)';
                      }}
                    >
                      <i className="ri-eye-line me-2" style={{ fontSize: '18px' }}></i>
                      View All {dashboardData.enlistCar.filter(v => v.booked === true).length} Booked Cars
                      <i className="ri-arrow-right-line ms-2" style={{ fontSize: '18px' }}></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Responsive CSS for Booked Cars Table */}
      <style>{`
        /* Mobile Responsive Styles */
        @media (max-width: 767.98px) {
          .booked-cars-icon {
            width: 48px !important;
            height: 48px !important;
          }
          .booked-cars-icon i {
            font-size: 20px !important;
          }
          .booked-cars-title {
            font-size: 1.1rem !important;
            line-height: 1.3 !important;
          }
          .booked-count-badge {
            font-size: 0.8rem !important;
            padding: 0.5rem 0.75rem !important;
          }
          .booked-count-badge i {
            font-size: 14px !important;
          }
          .booked-view-all-btn {
            padding: 0.5rem 1rem !important;
            font-size: 0.85rem !important;
          }
          .booked-cars-table {
            min-width: 600px !important;
          }
          .booked-table-header {
            padding: 0.75rem 0.5rem !important;
            font-size: 0.7rem !important;
          }
          .booked-table-header .rounded-circle {
            width: 24px !important;
            height: 24px !important;
          }
          .booked-table-header i {
            font-size: 12px !important;
          }
          .booked-cars-table td {
            padding: 0.75rem 0.5rem !important;
            font-size: 0.85rem !important;
          }
          .booked-cars-table .badge {
            font-size: 0.75rem !important;
            padding: 0.4rem 0.6rem !important;
          }
          .booked-col-title strong {
            font-size: 0.85rem !important;
          }
          .booked-col-price .badge {
            font-size: 0.8rem !important;
            padding: 0.5rem 0.75rem !important;
          }
          .booked-col-price .badge i {
            font-size: 14px !important;
          }
        }

        /* Tablet Responsive Styles */
        @media (min-width: 768px) and (max-width: 991.98px) {
          .booked-cars-table {
            min-width: 700px !important;
          }
          .booked-table-header {
            padding: 0.875rem 0.75rem !important;
            font-size: 0.75rem !important;
          }
          .booked-cars-table td {
            padding: 0.875rem 0.75rem !important;
          }
        }

        /* Small Desktop Responsive Styles */
        @media (min-width: 992px) and (max-width: 1199.98px) {
          .booked-cars-table {
            min-width: 900px !important;
          }
        }

        /* Touch device optimizations */
        @media (hover: none) and (pointer: coarse) {
          .booked-cars-table-wrapper {
            -webkit-overflow-scrolling: touch;
          }
          .booked-view-all-btn:active,
          .booked-cars-table tbody tr:active {
            transform: scale(0.98) !important;
          }
        }

        /* Print styles */
        @media print {
          .booked-cars-table-wrapper {
            overflow: visible !important;
          }
          .booked-view-all-btn,
          .booked-count-badge {
            display: none !important;
          }
          .booked-cars-table {
            min-width: 100% !important;
          }
          .booked-col-seller,
          .booked-col-payment,
          .booked-col-date {
            display: table-cell !important;
          }
        }

        /* Smooth scrolling for table */
        .booked-cars-table-wrapper::-webkit-scrollbar {
          height: 8px;
        }
        .booked-cars-table-wrapper::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .booked-cars-table-wrapper::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #667eea 0%, #0dcaf0 100%);
          border-radius: 10px;
        }
        .booked-cars-table-wrapper::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #764ba2 0%, #20c997 100%);
        }
      `}</style>
    </div>
  );
};

export default DashboardLayer;